/**
 * Pure geometry for the artwork crop editor (JARVIS-391): letterboxing the
 * image onto the editor stage, projecting corner handles to and from it, and
 * turning four marked corners into the homography that flattens them.
 *
 * THE QUAD LIVES IN IMAGE COORDINATES, not stage coordinates. That one
 * choice is what keeps rotation from infecting everything else.
 *
 * `StudentArtwork.rotation` is a plain number applied in CSS at render time
 * and never baked into stored pixels (owner decision, Oct 2026) — one angle
 * describes the original and its crop alike, so the two can never disagree
 * and a crop can still be turned afterwards. The editor shows the piece
 * already rotated, the way every other surface does.
 *
 * Because the corners are held in the original's own pixel space:
 *
 * - Rotating is free. The angle only affects how the quad is PROJECTED for
 *   display (`toStagePoint`) and how a drag is read back (`toRawImagePoint`),
 *   so the handles stay stuck to the same physical corners of the artwork
 *   and re-place themselves. Nothing is recalculated and nothing has to be
 *   disabled mid-drag.
 * - `planPerspectiveCrop` has no rotation term at all. The quad is already
 *   in the original's frame and already in its corner order, so it solves
 *   straight onto an upright rectangle, and the output lands in the frame
 *   the stored angle describes.
 *
 * With untouched corners the plan is a no-op pass over the whole image at
 * any rotation — the same contract as the mobile editor.
 *
 * No DOM imports — unit-testable in node.
 */
import { getScaledDimensions } from "#utils/artworkRenditionHelpers"
import { computeHomography, type Point2D, type Quad } from "#utils/homography"

export interface DisplayRect {
  x: number
  y: number
  width: number
  height: number
}

/** Corner order: TL, TR, BR, BL of the ORIGINAL, in its own pixel space. */
export type QuadPoints = [Point2D, Point2D, Point2D, Point2D]

const QUARTER_TURN_DEGREES = 90
const TURNS_PER_REVOLUTION = 4

/**
 * Rotation in whole clockwise quarter turns, 0-3. Angles are stored
 * normalized, but the rotation queue hands out cumulative values (450) and
 * legacy data can be negative, so both are folded here. A non-cardinal angle
 * would land between frames; it rounds to the nearest quarter turn rather
 * than throwing, because the crop still has to be possible on a bad record.
 */
export const getQuarterTurns = (rotation: number | undefined): number => {
  const turns = Math.round((rotation ?? 0) / QUARTER_TURN_DEGREES)
  return (
    ((turns % TURNS_PER_REVOLUTION) + TURNS_PER_REVOLUTION) %
    TURNS_PER_REVOLUTION
  )
}

export const isQuarterTurnRotation = (rotation: number | undefined): boolean =>
  getQuarterTurns(rotation) % 2 === 1

/** The image's on-screen dimensions: a quarter turn swaps the axes. */
export const getRotatedSize = (
  width: number,
  height: number,
  rotation: number | undefined
): { width: number; height: number } =>
  isQuarterTurnRotation(rotation)
    ? { width: height, height: width }
    : { width, height }

/**
 * Letterboxes a `width` x `height` image inside the stage, centred, inset by
 * `padding` on every side so a corner handle straddling the edge renders in
 * full instead of being clipped. The dimensions passed in are the ROTATED
 * ones — the stage shows the image the way the rest of the app does.
 */
export const fitDisplayRect = (
  width: number,
  height: number,
  stageWidth: number,
  stageHeight: number,
  padding: number
): DisplayRect | null => {
  if (width <= 0 || height <= 0) return null
  if (stageWidth <= 0 || stageHeight <= 0) return null
  const availableWidth = Math.max(1, stageWidth - padding * 2)
  const availableHeight = Math.max(1, stageHeight - padding * 2)
  const scale = Math.min(availableWidth / width, availableHeight / height)
  const fittedWidth = width * scale
  const fittedHeight = height * scale
  return {
    x: (stageWidth - fittedWidth) / 2,
    y: (stageHeight - fittedHeight) / 2,
    width: fittedWidth,
    height: fittedHeight,
  }
}

/**
 * Corners on the image's own edges: untouched handles mean "the whole
 * image", so Apply without adjustment is a no-op warp rather than silently
 * shaving an inset margin off. Same default as the mobile editor.
 */
export const defaultImageQuad = (width: number, height: number): QuadPoints => [
  { x: 0, y: 0 },
  { x: width, y: 0 },
  { x: width, y: height },
  { x: 0, y: height },
]

const clampTo = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/** Keeps a corner inside the image. Clamping in image space, not stage
 *  space, is what makes a corner stay put across a rotation or a resize. */
export const clampToImage = (
  point: Point2D,
  width: number,
  height: number
): Point2D => ({
  x: clampTo(point.x, 0, width),
  y: clampTo(point.y, 0, height),
})

/**
 * A pixel in the raw image to its position on the stage.
 *
 * The stage shows the image turned k quarter turns clockwise: raw (x, y)
 * lands at displayed (H - y, x) for k = 1, and so on round the circle. The
 * displayed pixel is then scaled and offset onto the fitted rect.
 */
export const toStagePoint = (
  point: Point2D,
  rect: DisplayRect,
  rawWidth: number,
  rawHeight: number,
  rotation: number | undefined
): Point2D => {
  const displayed = getRotatedSize(rawWidth, rawHeight, rotation)
  const turned = ((): Point2D => {
    switch (getQuarterTurns(rotation)) {
      case 1:
        return { x: rawHeight - point.y, y: point.x }
      case 2:
        return { x: rawWidth - point.x, y: rawHeight - point.y }
      case 3:
        return { x: point.y, y: rawWidth - point.x }
      default:
        return point
    }
  })()
  return {
    x: rect.x + (turned.x / displayed.width) * rect.width,
    y: rect.y + (turned.y / displayed.height) * rect.height,
  }
}

/** The inverse of `toStagePoint`: a stage point back to a raw image pixel. */
export const toRawImagePoint = (
  point: Point2D,
  rect: DisplayRect,
  rawWidth: number,
  rawHeight: number,
  rotation: number | undefined
): Point2D => {
  const displayed = getRotatedSize(rawWidth, rawHeight, rotation)
  const dx = ((point.x - rect.x) / rect.width) * displayed.width
  const dy = ((point.y - rect.y) / rect.height) * displayed.height

  switch (getQuarterTurns(rotation)) {
    case 1:
      return { x: dy, y: rawHeight - dx }
    case 2:
      return { x: rawWidth - dx, y: rawHeight - dy }
    case 3:
      return { x: rawWidth - dy, y: dx }
    default:
      return { x: dx, y: dy }
  }
}

const distance = (a: Point2D, b: Point2D): number =>
  Math.hypot(b.x - a.x, b.y - a.y)

/**
 * Averaging opposite edge lengths gives an output whose proportions
 * approximate the marked region's true (unwarped) aspect ratio. Same rule as
 * the mobile editor's `handleApply`.
 */
export const getOutputSize = (
  quad: Quad
): { width: number; height: number } => ({
  width: Math.max(
    1,
    Math.round((distance(quad[0], quad[1]) + distance(quad[3], quad[2])) / 2)
  ),
  height: Math.max(
    1,
    Math.round((distance(quad[0], quad[3]) + distance(quad[1], quad[2])) / 2)
  ),
})

export interface PerspectiveCropPlan {
  /** Row-major 3x3 mapping raw image pixels onto the output rectangle. */
  matrix: number[]
  /** The marked corners, carried through so the warp can re-derive it. */
  sourceQuad: Quad
  width: number
  height: number
}

export interface PerspectiveCropInput {
  /** The marked corners in RAW image pixels, TL/TR/BR/BL of the original. */
  quad: QuadPoints
  /**
   * Caps the output's long edge. The saved crop is capped anyway, so warping
   * more pixels than will ever be stored is wasted work — and an output past
   * the GPU's max texture size cannot be rendered at all. Scaling here rather
   * than afterwards keeps one matrix: the destination rectangle IS the
   * capped size, so the homography resamples straight into it.
   */
  maxOutputEdge?: number
}

/**
 * The Apply computation. Note the absence of any rotation argument: the quad
 * arrives in the original's frame, so the output does too, and the piece's
 * stored angle keeps turning it the right way up.
 *
 * Null when the corners do not form a usable shape (three collinear, two
 * coincident), which the editor surfaces as "adjust and retry" rather than
 * writing garbage over the piece every surface shows.
 */
export const planPerspectiveCrop = ({
  quad,
  maxOutputEdge,
}: PerspectiveCropInput): PerspectiveCropPlan | null => {
  const natural = getOutputSize(quad)
  const { width, height } = maxOutputEdge
    ? getScaledDimensions(natural.width, natural.height, maxOutputEdge)
    : natural

  const destinationQuad: Quad = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ]
  const matrix = computeHomography(quad, destinationQuad)
  if (!matrix) return null

  return { matrix, sourceQuad: quad, width, height }
}
